import { Link } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import SeoHead from "@/components/feature/SeoHead";
import Newsletter from "@/components/feature/Newsletter";
import { testimonials } from "@/mocks/content";

const origin = "https://zuori-chigua.example";

const process = [
  { n: "01", t: "锁定话题", d: "从昨天热议的公开事件里，选出入场者足够、信息量足够的话题，避免无米之炊。" },
  { n: "02", t: "拉取时间线", d: "把所有关键节点按时间排序，标明每条信息来自谁、何时发布，先对表再下笔。" },
  { n: "03", t: "逐帧比对", d: "对文字、图片、措辞做细节层面的并排观察，把可核对与不可核对的严格分开。" },
  { n: "04", t: "标注归档", d: "无法定论的内容一律归入「待证」或「未解之谜」，并附上编辑部说明后才发布。" },
];

const rules = [
  { icon: "ri-time-line", t: "时效优先", d: "尽量在事件发酵 24–48 小时内完成梳理，让信息差最小化。" },
  { icon: "ri-lock-2-line", t: "证据优先", d: "以平台公开、可复核的内容为准，来源不明的截图必须显著标注。" },
  { icon: "ri-user-unfollow-line", t: "对事不对人", d: "只梳理事实节点与公开表述，不替真实的人下道德或法律判断。" },
  { icon: "ri-refresh-line", t: "敢于纠错", d: "发现整理有误即核对修正，并在能力范围内同步说明更新点。" },
];

export default function About() {
  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "昨日吃瓜",
      url: origin,
      description:
        "聚焦昨日吃瓜细节分析的娱乐热点编辑部，整理时间线与隐藏线索，归档未解之谜。",
      sameAs: [origin],
    },
    {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "关于我们｜昨日吃瓜",
      url: origin + "/about",
      inLanguage: "zh-CN",
    },
  ];

  return (
    <>
      <SeoHead
        title="关于我们｜昨日吃瓜：一家只做复盘的娱乐热点编辑部"
        description="了解昨日吃瓜的品牌定位、发展理念与内容原则：先对表再吃瓜、只提问不定性、给来源讲边界。有问题欢迎通过邮件联系编辑部。"
        keywords="昨日吃瓜,关于我们,内容原则,编辑部,理性吃瓜"
        path="/about"
        ogType="website"
        jsonLd={schema}
      />
      <Header />
      <main>
        {/* page hero */}
        <section className="relative overflow-hidden border-b border-background-300/60 bg-background-50">
          <div className="pointer-events-none absolute -left-10 bottom-0 select-none font-display text-[12rem] font-black leading-none text-primary-100/50">
            关于
          </div>
          <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
            <nav className="mb-5 flex items-center gap-2 text-xs text-foreground-500" aria-label="面包屑">
              <Link to="/" className="flex items-center gap-1 hover:text-primary-600">
                <i className="ri-home-4-line" aria-hidden="true" /> 首页
              </Link>
              <i className="ri-arrow-right-s-line" aria-hidden="true" />
              <span className="text-foreground-700">关于我们</span>
            </nav>
            <p className="text-xs font-bold tracking-[0.25em] text-primary-600">ABOUT US</p>
            <h1 className="mt-3 max-w-3xl font-display text-3xl font-black leading-tight text-foreground-950 md:text-5xl">
              一家相信「吃瓜也有方法论」的编辑部
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-foreground-700 md:text-lg">
              我们做的是娱乐热点复盘，不追第一手刺激，只追第二手清晰。时间线、细节、措辞与疑点，
              是我们在每一篇里反复打磨的四件事。
            </p>
          </div>
        </section>

        {/* positioning */}
        <section className="border-b border-background-300/60 bg-background-100/70">
          <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
            <h2 className="text-center font-display text-2xl font-black text-foreground-950 md:text-3xl">
              我们把自己定位成三件事
            </h2>
            <div className="mx-auto mt-4 h-1 w-16 rounded-full bg-primary-500" />
            <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
              {[
                {
                  icon: "ri-file-chart-line",
                  t: "热点的「时间馆」",
                  d: "把散落各处的发言与节点串成可核对的时间轴，让后来者不必从零拼图。",
                  c: "bg-primary-100 text-primary-700",
                },
                {
                  icon: "ri-search-line",
                  t: "细节的「放大镜」",
                  d: "逐字逐帧观察图片、措辞与人称变化，帮读者看到一眼带过的内容。",
                  c: "bg-accent-100 text-accent-800",
                },
                {
                  icon: "ri-archive-drawer-line",
                  t: "疑点的「档案柜」",
                  d: "把尚无答案的未解之谜分类归档，只记录，不替任何人脑补结论。",
                  c: "bg-secondary-200 text-secondary-900",
                },
              ].map((x, i) => (
                <div key={i} className="rounded-2xl bg-background-50 p-7 text-center ring-1 ring-background-200/70">
                  <span className={`mx-auto flex h-14 w-14 items-center justify-center rounded-2xl text-2xl ${x.c}`}>
                    <i className={x.icon} aria-hidden="true" />
                  </span>
                  <h3 className="mt-4 font-display text-xl font-bold text-foreground-950">{x.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{x.d}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* process */}
        <section className="bg-background-50">
          <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
            <div className="mb-10 max-w-2xl">
              <p className="text-xs font-bold tracking-[0.25em] text-primary-600">发展理念 · 编辑部的工作流</p>
              <h2 className="mt-2 font-display text-2xl font-black text-foreground-950 md:text-3xl">
                一篇复盘，是怎么从热搜走到你面前的
              </h2>
            </div>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {process.map((s, i) => (
                <div key={i} className="relative rounded-2xl bg-background-100 p-6">
                  <span className="font-display text-4xl font-black text-primary-200">{s.n}</span>
                  <h3 className="mt-2 font-display text-lg font-bold text-foreground-950">{s.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{s.d}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* principles */}
        <section className="border-y border-background-300/60 bg-primary-700 text-background-100">
          <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
            <h2 className="text-center font-display text-2xl font-black text-background-50 md:text-3xl">
              内容原则，写在所有文章之前
            </h2>
            <div className="mx-auto mt-4 h-1 w-16 rounded-full bg-background-50/60" />
            <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {rules.map((r, i) => (
                <div key={i} className="rounded-2xl bg-background-50/10 p-6">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-background-50/15 text-background-50">
                    <i className={`${r.icon} text-xl`} aria-hidden="true" />
                  </span>
                  <h3 className="mt-4 font-display text-lg font-bold text-background-50">{r.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-background-200">{r.d}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* testimonials */}
        <section className="bg-background-100/60">
          <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
            <h2 className="text-center font-display text-2xl font-black text-foreground-950 md:text-3xl">
              大家的口碑，是最好的编辑部介绍
            </h2>
            <div className="mt-8 grid grid-cols-1 gap-5 lg:grid-cols-3">
              {testimonials.map((t, i) => (
                <figure key={i} className="rounded-2xl bg-background-50 p-6 ring-1 ring-background-200/70">
                  <i className="ri-double-quotes-l text-3xl text-accent-300" aria-hidden="true" />
                  <blockquote className="mt-2 flex-1 text-sm leading-relaxed text-foreground-700">
                    {t.quote}
                  </blockquote>
                  <figcaption className="mt-5 border-t border-background-200/70 pt-4">
                    <span className="block text-sm font-semibold text-foreground-900">{t.author}</span>
                    <span className="block text-xs text-foreground-500">{t.role}</span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        {/* contact */}
        <section id="contact" className="bg-background-50">
          <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16">
              <div>
                <p className="text-xs font-bold tracking-[0.25em] text-primary-600">联系方式</p>
                <h2 className="mt-2 font-display text-2xl font-black text-foreground-950 md:text-3xl">
                  来信聊聊，或直接订阅
                </h2>
                <p className="mt-4 max-w-xl text-base leading-relaxed text-foreground-600">
                  发现信息有误、想投稿话题、或只是对某篇复盘有不同看法，都欢迎来信。编辑部会在核对后认真回应。
                </p>
                <ul className="mt-6 space-y-3 text-sm text-foreground-800">
                  <li className="flex items-center gap-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                      <i className="ri-mail-line" aria-hidden="true" />
                    </span>
                    hello@zuori-chigua.example
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent-100 text-accent-800">
                      <i className="ri-chat-3-line" aria-hidden="true" />
                    </span>
                    每天 10:00 - 22:00 回复来信
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-secondary-200 text-secondary-900">
                      <i className="ri-feedback-line" aria-hidden="true" />
                    </span>
                    投稿 / 纠错请在标题注明
                  </li>
                </ul>
              </div>
              <div className="rounded-2xl bg-background-100 p-7 md:p-9">
                <h3 className="font-display text-xl font-black text-foreground-950">订阅「昨日瓜报」</h3>
                <p className="mt-2 text-sm text-foreground-600">
                  每期完整复盘、栏目更新与专题特辑，直送你的邮箱。
                </p>
                <div className="mt-5">
                  <Newsletter />
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}